// Pitch Match state machine (issue #3518): a pure reducer over one session of ten notes, driven by
// microphone frames and the page's actions. No React, no DOM, no timers, no audio. The page holds
// the state in useReducer, subscribes to the Learn audio session's frames and dispatches each one,
// plays the piano guide once per guide request and dispatches `guideStarted` when it is scheduled,
// polls the audio clock and dispatches `tick`, and renders from the state and the selectors below.
// Stories and tests drive it the same way with synthetic frames.
//
// Every time in this module is absolute ms on the audio clock: frames carry it (CaptureFrame.timeMs),
// `guideStarted` carries the time the guide really started at (which is not always the time it was
// asked for: a slow soundfont load moves the note to the clock's reading now) and `tick` is the
// page's reading of the same clock. Nothing here reads a wall clock, so the elapsed times that decide a lock, a
// nudge and a miss come only from the frames the mic delivers.
//
// Phases:
//
//   note ──lock or miss, then the pause──▶ note ──▶ ... ──▶ note ──▶ summary (the page opens the next session)
//     │
//     │ micFailed, or tooNoisyAfterMs of sound with no note in it
//     ▼
//   failed ──retry──▶ note (the same target again, from a fresh guide)
//
// One note. The page plays the target on the piano (never a bare tone) once and reports when it
// sounded; the machine then scores frames against it until the singer locks it, the note times
// out, or they skip it. A lock or a miss holds the screen for the pause, then the next target
// opens with its own guide request; after the last target the session is complete.
//
// The note is never replayed on the machine's account (issue #3518, Michael's feedback): the
// guide plays once, rings for as long as the piano's own sample rings, and comes back only when
// the singer taps "Play again". Five seconds of silence is therefore text and nothing more - it
// sets `nudged`, which shows the line naming both of those, and asks for no sound and counts
// nothing. A note nobody resolves ends one of two ways: noteTimeoutMs of scored time on one guide
// with no lock (a device whose echo canceller leaves the piano's own sound above voicedDbfs never
// accumulates silence, so this is the backstop that ends such a note at all), or Skip.
//
// The two modes differ only in when scoring starts and how long the guide is scheduled for. In
// play-then-listen the note sounds for guideMs and the machine ignores every frame until
// guideMs + guideTailMs after it started, so the guide is never scored as the singer. In
// sing-along the guide keeps sounding while they sing - it is scheduled for singAlongGuideMs, the
// piano's own ring - and frames are scored from the moment it started. `guideEndsMs` is when the
// note it asked for stops sounding, which is all `isGuideSounding` needs to tell the screen
// whether the reference is still in the air (its "Play again" is outlined while it is, solid once
// it has faded); the machine reads it against `clockMs`, the latest time any frame or tick has
// carried, so nothing here needs a wall clock. `isGuideRinging` is the narrower question - a note
// that has actually started and not yet faded - and it is the one the page's clock loop is gated
// on, so a slow soundfont load is waited out without an animation frame a second polling through it.
//
// Reading a frame against the target - the detectors and the cross-check between them, the octave
// rule, continuity with the voice, the smoothing and the hold - lives in note_scorer.ts, which every
// Learn exercise that asks for a sung note shares (issue #3721). The machine decides which frames
// it scores: a frame while the guide is sounding or has not been reported yet, or after the note
// has its outcome, goes to hearFrame, which takes the meter level and the frame's time and scores
// nothing; every other frame goes to scoreFrame with the level's params, whose `locked` and
// `tooNoisy` become the note's lock and the too-noisy screen. The scored time and the silence nudge
// are the machine's own.

import { learnListeningTunables, pitchLevelParams, pitchMatchTunables } from '../../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../../common/src/model.ts';
import { frameDelta } from '../audio/frame_reading.ts';
import { CaptureFrame, MicFailure } from '../audio/learn_mic_capture.ts';
import {
  freshNoteScore,
  hearFrame,
  holdProgressFor,
  noReading,
  NoteScore,
  octaveText,
  pitchHigherText,
  pitchHoldText,
  pitchLouderText,
  pitchLowerText,
  readingDot,
  ReadingHint,
  readingHint,
  scoreFrame,
} from './note_scorer.ts';

// The listening thresholds every Learn exercise shares, plus Pitch Match's own timings; both live
// in learn_rules.ts so the device validation pass (issue #3338, slice 8) tunes them in one place.
const tunables = { ...learnListeningTunables, ...pitchMatchTunables };

// ---- State -----------------------------------------------------------------------------------

export type PitchMode = 'singAlong' | 'playThenListen';

export type PitchNoteResult = 'locked' | 'missed';

export type PitchFailure = MicFailure | 'tooNoisy';

export type PitchPhase = 'note' | 'summary' | 'failed';

// One note: the scorer's fields (note_scorer.ts) and the machine's own
export interface PitchNoteState extends NoteScore {
  targetMidi: number;
  // The page plays each id once, on this note's targetMidi; a new one is the same note again,
  // because the singer tapped "Play again"
  guideRequestId: number;
  guideAtMs?: number; // when the current guide request sounded (from guideStarted)
  guideEndsMs?: number; // when it stops sounding: guideAtMs plus the mode's guide length
  listenFromMs?: number; // frames before it are not scored
  listeningMs: number; // scored time on the current guide, so a note nobody resolves still ends
  clockMs?: number; // the latest time the machine has seen, from a frame or a tick
  nudged: boolean; // the silence nudge is showing
  outcome?: { kind: PitchNoteResult; atMs: number };
}

export interface PitchSessionState {
  phase: PitchPhase;
  level: LearnLevel;
  mode: PitchMode;
  targets: number[];
  results: PitchNoteResult[]; // one per finished note; its length is the current note's index
  note: PitchNoteState; // the current note (or, in summary/failed, the last one)
  failure?: PitchFailure;
  nextRequestId: number;
}

// ---- Actions ---------------------------------------------------------------------------------

export type PitchSessionAction =
  | { type: 'frame'; frame: CaptureFrame } // one analysed frame from the mic
  | { type: 'guideStarted'; requestId: number; atMs: number } // the page scheduled the guide for `atMs`
  | { type: 'tick'; nowMs: number } // the audio clock, during the pauses and while the guide rings
  | { type: 'replay' } // "Play again": the one way the guide ever sounds a second time
  | { type: 'skip'; nowMs: number } // "Skip this note", stamped with the page's reading of the clock
  | { type: 'micFailed'; failure: MicFailure }
  | { type: 'retry' }; // from 'failed': the mic is open again, resume the same note

// A session at its beginning. The page builds one per run - the Start tap and "Play again" alike -
// so the request ids start again from 1 with it; nothing outside a session ever sees them.
export function initialPitchSessionState({
  level,
  mode,
  targets,
}: {
  level: LearnLevel;
  mode: PitchMode;
  targets: number[];
}): PitchSessionState {
  return sessionState(level, mode, targets, 1);
}

export function reducePitchSession(state: PitchSessionState, action: PitchSessionAction): PitchSessionState {
  switch (action.type) {
    case 'frame':
      return frame(state, action.frame);
    case 'guideStarted':
      return guideStarted(state, action.requestId, action.atMs);
    case 'tick':
      return tick(state, action.nowMs);
    case 'replay':
      return replay(state);
    case 'skip':
      return skip(state, action.nowMs);
    case 'micFailed':
      return state.phase === 'summary' ? state : { ...state, phase: 'failed', failure: action.failure };
    case 'retry':
      return retry(state);
  }
}

// ---- Transitions -----------------------------------------------------------------------------

function sessionState(
  level: LearnLevel,
  mode: PitchMode,
  targets: number[],
  firstRequestId: number,
): PitchSessionState {
  return {
    phase: 'note',
    level,
    mode,
    targets,
    results: [],
    note: { ...freshNote(targets[0]), guideRequestId: firstRequestId },
    nextRequestId: firstRequestId + 1,
  };
}

// A note at its beginning: nothing accumulated and everything the note before it left behind
// cleared, ready for its first guide request. The meter level and the audio clock carry over from
// that note, so the meter does not blink and the first frame of the new note accounts for its own
// elapsed time rather than none.
function freshNote(
  targetMidi: number,
  previous?: PitchNoteState,
): Omit<PitchNoteState, 'guideRequestId' | 'guideAtMs'> {
  return {
    ...freshNoteScore(previous),
    targetMidi,
    guideEndsMs: undefined,
    listenFromMs: undefined,
    listeningMs: 0,
    clockMs: previous?.clockMs,
    nudged: false,
    outcome: undefined,
  };
}

function withNote(state: PitchSessionState, note: PitchNoteState): PitchSessionState {
  return { ...state, note };
}

// Ask the page for the guide: the next request id, which it plays once, and its start not yet
// reported. `guideEndsMs` is deliberately left where it was, so the reference the singer can still
// hear counts as sounding through the lead between the request and the note. The patch is
// everything else this case changes about the note - a fresh note replaces the lot, and a manual
// replay in play-then-listen closes the listening window again (listenAgain below).
function requestGuide(state: PitchSessionState, patch: Partial<PitchNoteState>): PitchSessionState {
  return {
    ...state,
    note: { ...state.note, guideRequestId: state.nextRequestId, guideAtMs: undefined, ...patch },
    nextRequestId: state.nextRequestId + 1,
  };
}

// Nothing is scored until the new guide sounds, and neither the silence nor the listening time
// spent on the guide before it counts against the one coming.
const listenAgain: Partial<PitchNoteState> = {
  listenFromMs: undefined,
  listeningMs: 0,
  silentMs: 0,
  hold: undefined,
};

function guideStarted(state: PitchSessionState, requestId: number, atMs: number): PitchSessionState {
  const note = state.note;
  if (state.phase !== 'note' || note.guideRequestId !== requestId) {
    return state; // a request the machine has since replaced
  }
  // Play-then-listen waits out the note and its tail; sing-along scores from the moment the guide
  // starts, and a replay mid-note leaves the window it already opened alone.
  const listenFromMs =
    state.mode === 'singAlong' ? (note.listenFromMs ?? atMs) : atMs + tunables.guideMs + tunables.guideTailMs;
  const guideEndsMs = atMs + (state.mode === 'singAlong' ? tunables.singAlongGuideMs : tunables.guideMs);
  return withNote(state, { ...note, guideAtMs: atMs, guideEndsMs, listenFromMs });
}

// "Play again", the one thing that ever plays the note a second time. In play-then-listen the
// guide is the exercise, so scoring pauses for it exactly as it did for the first one. In
// sing-along it does not: the singer may well be holding the note while they reach for the button,
// and blanking the hold and the reading under them would punish them for asking to hear it again.
// The note's own listening clock starts over either way, because the wait they asked for is not a
// wait they should be timed out for.
//
// A guide already asked for and not yet sounding swallows the tap: on a slow soundfont load the
// button sits under "Listen…" for a moment with a note on its way, and a second request there
// would put a second note in the air behind the first (issue #3518).
function replay(state: PitchSessionState): PitchSessionState {
  if (state.phase !== 'note' || state.note.outcome || state.note.guideAtMs === undefined) {
    return state;
  }
  if (state.mode === 'singAlong') {
    return requestGuide(state, { listeningMs: 0, silentMs: 0, nudged: false });
  }
  return requestGuide(state, { ...listenAgain, ...noReading, nudged: false });
}

// "Skip this note": missed, stamped with the page's clock rather than the last frame's, so a skip
// before any frame arrived still holds the miss pause for its full length.
function skip(state: PitchSessionState, nowMs: number): PitchSessionState {
  const note = state.note;
  if (state.phase !== 'note' || note.outcome) {
    return state;
  }
  return withNote(state, { ...note, outcome: { kind: 'missed', atMs: nowMs } });
}

function retry(state: PitchSessionState): PitchSessionState {
  if (state.phase !== 'failed') {
    return state;
  }
  return {
    ...requestGuide(state, freshNote(state.note.targetMidi, state.note)),
    phase: 'note',
    failure: undefined,
  };
}

// A lock or a miss holds the screen for its pause; the tick (or the next frame) that reaches the
// end of it records the result and opens the next note, or ends the session after the last one.
function afterPause(state: PitchSessionState, nowMs: number): PitchSessionState {
  const outcome = state.note.outcome;
  if (state.phase !== 'note' || !outcome) {
    return state;
  }
  const pauseMs = outcome.kind === 'locked' ? tunables.lockPauseMs : tunables.missPauseMs;
  if (nowMs < outcome.atMs + pauseMs) {
    return state;
  }
  const results: PitchNoteResult[] = [...state.results, outcome.kind];
  if (results.length >= state.targets.length) {
    return { ...state, phase: 'summary', results };
  }
  return { ...requestGuide(state, freshNote(state.targets[results.length], state.note)), results };
}

// A note nobody resolves has to end, and since nothing is replayed automatically this is the only
// way one ends without a lock or a Skip: silence alone never ends a note (a device whose echo
// canceller leaves the piano's own sound above voicedDbfs accumulates none of it anyway), so this
// long spent scoring one guide with no outcome misses it.
function afterNoteTimeout(state: PitchSessionState, timeMs: number): PitchSessionState {
  const note = state.note;
  if (state.phase !== 'note' || note.outcome || note.listeningMs < tunables.noteTimeoutMs) {
    return state;
  }
  return withNote(state, { ...note, outcome: { kind: 'missed', atMs: timeMs } });
}

// The latest time the machine has seen, which is what isGuideSounding reads instead of a wall
// clock. The note itself comes back when the reading is not new, so a tick that tells the machine
// nothing - most of them, since the page polls the clock every animation frame - is a no-op the
// reducer's caller can bail out of rather than a re-render.
function withClock(note: PitchNoteState, nowMs: number): PitchNoteState {
  if (note.clockMs !== undefined && note.clockMs >= nowMs) {
    return note;
  }
  return { ...note, clockMs: nowMs };
}

function tick(state: PitchSessionState, nowMs: number): PitchSessionState {
  if (state.phase !== 'note') {
    return state;
  }
  const note = withClock(state.note, nowMs);
  return afterPause(note === state.note ? state : withNote(state, note), nowMs);
}

// ---- Frames ----------------------------------------------------------------------------------

function frame(state: PitchSessionState, frame: CaptureFrame): PitchSessionState {
  if (state.phase !== 'note') {
    return state;
  }
  return afterNoteTimeout(takeFrame(state, frame), frame.timeMs);
}

// The note's own clock, and then the scorer: hearFrame for a frame the machine does not score,
// scoreFrame for one it does (see the header)
function takeFrame(state: PitchSessionState, frame: CaptureFrame): PitchSessionState {
  const note = withClock(state.note, frame.timeMs);
  if (note.outcome) {
    return afterPause(withNote(state, hearFrame(note, frame)), frame.timeMs); // the pause can end without a tick
  }
  if (note.listenFromMs === undefined || frame.timeMs < note.listenFromMs) {
    return withNote(state, hearFrame(note, frame)); // the guide is sounding, or has not been reported yet
  }
  const delta = frameDelta(note.lastFrameMs, frame.timeMs);
  const { score, locked, tooNoisy } = scoreFrame(note, frame, note.targetMidi, pitchLevelParams[state.level]);
  // Scored: this frame's time counts against the note's own limit as well as the scorer's
  // accumulators. Five seconds of nothing is a line of text and no more: the nudge asks them to
  // sing, or to hear the note again. Nothing is replayed and nothing is counted.
  const scored: PitchNoteState = {
    ...score,
    listeningMs: note.listeningMs + delta,
    nudged: note.nudged || score.silentMs >= tunables.silenceReplayMs,
    outcome: locked ? { kind: 'locked', atMs: frame.timeMs } : score.outcome,
  };
  if (tooNoisy) {
    return { ...state, phase: 'failed', failure: 'tooNoisy', note: scored };
  }
  return withNote(state, scored);
}

// ---- Selectors -------------------------------------------------------------------------------

export type PitchHint = 'listen' | 'sing' | ReadingHint | 'nudge' | 'locked' | 'missed';

export const pitchListenText = 'Listen…';
export const pitchSingText = 'Sing it back';
// One line for both modes: in play-then-listen nothing is sounding to sing along with, so the
// nudge names the two things there are to do rather than one that is not there (issue #3518)
export const pitchNudgeText = 'Sing "La", or tap Play again to hear the note';
export const pitchMissedText = 'Missed';

export const pitchHintTexts: Record<PitchHint, string> = {
  listen: pitchListenText,
  sing: pitchSingText,
  higher: pitchHigherText,
  lower: pitchLowerText,
  louder: pitchLouderText,
  hold: pitchHoldText,
  nudge: pitchNudgeText,
  locked: pitchHoldText,
  missed: pitchMissedText,
};

export function pitchHint(state: PitchSessionState): PitchHint {
  const note = state.note;
  if (note.outcome) {
    return note.outcome.kind === 'locked' ? 'locked' : 'missed';
  }
  if (note.reading) {
    return readingHint(note.reading);
  }
  if (note.nudged) {
    return 'nudge';
  }
  return state.mode === 'playThenListen' && !hasBegunListening(note) ? 'listen' : 'sing';
}

export function pitchHintText(hint: PitchHint): string {
  return pitchHintTexts[hint];
}

export function octaveNoteText(state: PitchSessionState): string | undefined {
  return octaveText(state.note.reading);
}

export function holdProgress(state: PitchSessionState): { heldMs: number; holdMs: number } {
  return holdProgressFor(state.note, pitchLevelParams[state.level], state.note.outcome?.kind === 'locked');
}

// Where to draw the singer on the staff, and in which colour (readingDot); a missed note shows none
export function dotFor(state: PitchSessionState): { cents: number; color: 'yellow' | 'green' } | undefined {
  const { reading, outcome } = state.note;
  if (outcome?.kind === 'missed') {
    return undefined;
  }
  return readingDot(reading, outcome?.kind === 'locked');
}

function hasBegunListening(note: PitchNoteState): boolean {
  return note.listenFromMs !== undefined && note.lastFrameMs !== undefined && note.lastFrameMs >= note.listenFromMs;
}

// The guide has sounded, frames are being scored and the singer has not resolved the note yet
export function isListening(state: PitchSessionState): boolean {
  return state.phase === 'note' && !state.note.outcome && hasBegunListening(state.note);
}

// The piano is ringing the guide it was last asked for, or is about to. Read off the note's own
// times - the guide was scheduled to sound until guideEndsMs, and clockMs is the latest reading any
// frame or tick has carried - so the screen can offer "Play again" as the obvious next thing to do
// the moment the reference has faded, without anything here consulting a wall clock.
//
// A guide that has been asked for and has not reported back yet counts as sounding: the note is on
// its way (the soundfont may still be loading), so offering "Play again" as the thing to do would
// be offering a note that is about to arrive anyway.
export function isGuideSounding(state: PitchSessionState): boolean {
  if (state.phase !== 'note') {
    return false;
  }
  if (state.note.guideAtMs === undefined) {
    return true; // requested, not yet sounding
  }
  return isGuideRinging(state);
}

// The piano is in the air now: the guide has reported the time it really started and the clock has
// not reached the end of its ring. This is the one the page's clock loop is gated on. A guide that
// has only been asked for rings nothing yet - a cold soundfont can take seconds to load - and
// polling the clock through that load would be an animation frame a second doing nothing at all,
// so the loop waits for the note to sound.
export function isGuideRinging(state: PitchSessionState): boolean {
  const { guideAtMs, guideEndsMs, clockMs } = state.note;
  if (state.phase !== 'note' || guideAtMs === undefined || guideEndsMs === undefined) {
    return false;
  }
  return (clockMs ?? 0) < guideEndsMs;
}

// 0-based, the note the session is on: one result is recorded as each note ends, so the number of
// them is the index of the note in hand (and, on the summary, the length of the whole session)
export function pitchNoteIndex(state: PitchSessionState): number {
  return state.results.length;
}

export function sessionScore(results: PitchNoteResult[]): number {
  return results.filter((result) => result === 'locked').length;
}

export function bestStreak(results: PitchNoteResult[]): number {
  let best = 0;
  let run = 0;
  for (const result of results) {
    run = result === 'locked' ? run + 1 : 0;
    best = Math.max(best, run);
  }
  return best;
}

export function summarySquares(results: PitchNoteResult[]): Array<'green' | 'red'> {
  return results.map((result) => (result === 'locked' ? 'green' : 'red'));
}
